import Box from "@material-ui/core/Box";
import React, {PropsWithChildren} from "react";
import {
  BasicInput,
  BasicSelect,
  Checkbox,
  DatePicker,
  MultilineInput,
} from "view/components/Form";
import RichTextEditor from "view/components/RichTextEditor";
import {Control, Controller} from "react-hook-form";
import {Field, SettingValues} from "../types";

type ModalFieldProps<T extends SettingValues> = {
  field: Field<T>;
  defaultValue?: string;
  control?: Control;
};

const ModalField = <T extends SettingValues>({
  field,
  defaultValue,
  control,
}: PropsWithChildren<ModalFieldProps<T>>) => {
  if (field.type === "date") {
    return (
      <DatePicker
        defaultValue={field?.defaultValue}
        name={field.name}
        label={field.label}
        rules={field.validation}
        minDate={field.minDate}
        format={field.dateFormat}
      />
    );
  }

  if (field.type === "checkbox") {
    return (
      <Box display="flex" pl="11px">
        <Checkbox
          color="primary"
          size="small"
          label={field.label}
          name={field.name}
          defaultChecked={Boolean(field.defaultValue)}
        />
      </Box>
    );
  }

  if (field.type === "multiselect" && field?.items?.length) {
    return (
      <BasicSelect
        label={String(field.label || field.title)}
        name={String(field.name)}
        options={field.items}
        disabled={field?.disabled}
        defaultValue={field.name}
      />
    );
  }

  if (field.name === "paragraph") {
    return (
      <Controller
        control={control}
        name={field.name}
        render={({field: {onChange}}) => (
          <RichTextEditor
            id="paragraph"
            onChange={(val) => onChange(val)}
            defaultValue={defaultValue}
          />
        )}
      />
    );
  }

  const inputProps = {
    key: `${String(field.name)}`,
    name: String(field.name),
    type: field?.type || "text",
    label: field.label,
    uppercase: field.uppercase,
    rules: field.validation,
    multiline: field.multiline,
    disabled: field?.disabled,
    autoFocus: field.autoFocus,
    required: !!field.validation?.required,
    InputProps: field.InputProps,
    showHelperText: true,
  };
  if (field.type === "text" && field.multiline) {
    return <MultilineInput {...inputProps} multiline={field.multiline} />;
  }

  return <BasicInput {...inputProps} />;
};

export default ModalField;
